import { useState, useMemo, useEffect } from 'react';
import moment from 'moment';

/**
 * Custom hook for filtering activity feed events
 * Provides client-side filtering with date range, search, and event type filters
 */
const useActivityFeedFilters = (events) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);

  // Debounce search term (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Apply all filters with performance optimization. The three filter
  // predicates (date range / search / event types) are defined inline here
  // (rather than as separate hook-scope functions) so their closed-over
  // state doesn't need to be duplicated in this useMemo's dependency array
  // (issue #238 ESLint cleanup — react-hooks/exhaustive-deps); they aren't
  // used anywhere else in the hook.
  const filteredEvents = useMemo(() => {
    const filterByDateRange = (event) => {
      if (!dateRange.start && !dateRange.end) return true;

      const eventDate = moment(event.timestamp);

      if (dateRange.start && dateRange.end) {
        return eventDate.isBetween(dateRange.start, dateRange.end, 'day', '[]');
      }

      if (dateRange.start) {
        return eventDate.isSameOrAfter(dateRange.start, 'day');
      }

      if (dateRange.end) {
        return eventDate.isSameOrBefore(dateRange.end, 'day');
      }

      return true;
    };

    // Filter by search term (searches event type, description, metadata)
    const filterBySearch = (event) => {
      if (!debouncedSearchTerm) return true;

      const searchLower = debouncedSearchTerm.toLowerCase();

      // Search in event type
      if (event.eventType.toLowerCase().includes(searchLower)) return true;

      // Search in description
      if (event.description && event.description.toLowerCase().includes(searchLower)) return true;

      // Search in metadata
      if (event.metadata) {
        const metadataString = JSON.stringify(event.metadata).toLowerCase();
        if (metadataString.includes(searchLower)) return true;
      }

      // Search in severity
      if (event.severity && event.severity.toLowerCase().includes(searchLower)) return true;

      return false;
    };

    // Filter by event types
    const filterByEventTypes = (event) => {
      if (selectedEventTypes.length === 0) return true;
      return selectedEventTypes.includes(event.eventType);
    };

    return events.filter(event => {
      return (
        filterByDateRange(event) &&
        filterBySearch(event) &&
        filterByEventTypes(event)
      );
    });
  }, [events, dateRange, debouncedSearchTerm, selectedEventTypes]);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setDateRange({ start: null, end: null });
    setSelectedEventTypes([]);
  };

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (debouncedSearchTerm) count++;
    if (dateRange.start || dateRange.end) count++;
    if (selectedEventTypes.length > 0) count++;
    return count;
  }, [debouncedSearchTerm, dateRange, selectedEventTypes]);

  // Date range presets
  const applyDatePreset = (preset) => {
    switch (preset) {
      case 'today':
        setDateRange({
          start: moment().startOf('day').toDate(),
          end: moment().endOf('day').toDate()
        });
        break;
      case 'last7days':
        setDateRange({
          start: moment().subtract(7, 'days').startOf('day').toDate(),
          end: moment().endOf('day').toDate()
        });
        break;
      case 'last30days':
        setDateRange({
          start: moment().subtract(30, 'days').startOf('day').toDate(),
          end: moment().endOf('day').toDate()
        });
        break;
      case 'alltime':
        setDateRange({ start: null, end: null });
        break;
      default:
        break;
    }
  };

  return {
    // Filtered results
    filteredEvents,

    // Search state
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,

    // Date range state
    dateRange,
    setDateRange,
    applyDatePreset,

    // Event type state
    selectedEventTypes,
    setSelectedEventTypes,

    // Utilities
    clearFilters,
    activeFilterCount
  };
};

export default useActivityFeedFilters;
